/**
 * Daily streak tracking.
 *
 * Streaks are computed on *local calendar days*, not on elapsed hours — opening
 * the app at 11pm and again at 8am the next morning is a two-day streak. Day
 * keys are `YYYY-MM-DD` strings built from local date parts, and the gap
 * between two keys is measured with UTC arithmetic on those parts so daylight
 * saving transitions (23- and 25-hour days) can't produce a 0- or 2-day jump.
 */

const MS_PER_DAY = 86_400_000;

const pad = (n) => String(n).padStart(2, '0');

/** Local calendar day as `YYYY-MM-DD`. */
export function toDayKey(date = new Date()) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Whole days from `fromKey` to `toKey`. Negative if `toKey` is earlier. */
export function dayDiff(fromKey, toKey) {
  const [fy, fm, fd] = fromKey.split('-').map(Number);
  const [ty, tm, td] = toKey.split('-').map(Number);
  return Math.round(
    (Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / MS_PER_DAY,
  );
}

export const EMPTY_STREAK = {
  current: 0,
  longest: 0,
  lastVisit: null, // day key of the last counted visit
  totalDays: 0,
};

/**
 * Record a visit. Pure — returns a new streak object (or the same reference
 * when nothing changed, so callers can skip a re-render).
 *
 * - same day as the last visit → unchanged
 * - next calendar day          → +1
 * - a gap of 2+ days           → reset to 1
 * - a key *earlier* than the last visit (clock moved back, timezone travel)
 *   → unchanged, so a wound-back clock can't erase a streak
 */
export function recordVisit(streak = EMPTY_STREAK, today = toDayKey()) {
  if (!streak.lastVisit) {
    return { current: 1, longest: Math.max(1, streak.longest || 0), lastVisit: today, totalDays: 1 };
  }

  const gap = dayDiff(streak.lastVisit, today);
  if (gap <= 0) return streak;

  const current = gap === 1 ? streak.current + 1 : 1;
  return {
    current,
    longest: Math.max(current, streak.longest || 0),
    lastVisit: today,
    totalDays: (streak.totalDays || 0) + 1,
  };
}

/**
 * Whether a streak is stale — the user hasn't opened the app today *or*
 * yesterday, so `current` no longer reflects an unbroken run. Used to show the
 * header flame greyed out before the visit for today is recorded.
 */
export function isStreakBroken(streak, today = toDayKey()) {
  if (!streak?.lastVisit) return true;
  return dayDiff(streak.lastVisit, today) > 1;
}

/** Days-in-a-row milestones worth celebrating. */
export const STREAK_MILESTONES = [3, 7, 14, 30, 50, 100, 365];

export function nextMilestone(current) {
  return STREAK_MILESTONES.find((m) => m > current) ?? null;
}
